import React, { useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 32px;
  
  img {
    width: 200px;
    height: auto;
    margin-bottom: 24px;
  }
  
  h1 {
    margin: 0 0 8px 0;
    font-size: 28px;
    color: #24292f;
    font-weight: 700;
  }
  
  p {
    margin: 0;
    color: #57606a;
    font-size: 14px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Input = styled.input`
  padding: 14px 16px;
  border: 2px solid #d0d7de;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #0969da;
    box-shadow: 0 0 0 3px rgba(9,105,218,0.1);
  }
`;

const Button = styled.button`
  padding: 14px;
  background: linear-gradient(135deg, #0969da 0%, #0860ca 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(9,105,218,0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0;
  color: #57606a;
  font-size: 13px;
  
  &:before, &:after {
    content: '';
    flex: 1;
    height: 1px;
    background: #d0d7de;
  }
`;

const SSOButton = styled.button`
  padding: 12px;
  background: white;
  border: 2px solid #d0d7de;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    border-color: #0969da;
    background: #f6f8fa;
  }
`;

const Error = styled.div`
  padding: 12px;
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  color: #cf222e;
  font-size: 13px;
`;

const Link = styled.button`
  background: none;
  border: none;
  color: #0969da;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  
  &:hover {
    color: #0860ca;
  }
`;

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const api = await import('../utils/api');
      const { token, user } = await api.login(email, password);
      
      localStorage.setItem('auth-token', token);
      localStorage.setItem('current-user', JSON.stringify(user));
      
      setTimeout(() => {
        setLoading(false);
        onLogin(user, token);
      }, 500);
    } catch (err) {
      setError(err.message || 'Invalid email or password');
      setLoading(false);
    }
  };

  const handleSSO = () => {
    const ssoConfig = JSON.parse(localStorage.getItem('sso-config') || '{}');
    
    if (!ssoConfig.clientId || !ssoConfig.authUrl) {
      setError('SSO not configured. Please contact administrator.');
      return;
    }

    const params = new URLSearchParams({
      client_id: ssoConfig.clientId,
      redirect_uri: ssoConfig.redirectUri || window.location.origin + '/callback',
      response_type: 'code',
      scope: ssoConfig.scope || 'openid profile email',
      state: btoa(Math.random().toString())
    });

    window.location.href = `${ssoConfig.authUrl}?${params.toString()}`;
  };

  const handleDemoLogin = () => {
    setEmail('sagarmichael@example.com');
    setPassword('Kn0w!99nine');
  };

  return (
    <Container>
      <Card>
        <Logo>
          <img src={require('../Dlogo.png')} alt="SwaggerEditor Logo" />
          <h1>OpenAPI 3.0 Editor</h1>
          <p>Sign in to continue</p>
        </Logo>

        {error && <Error>{error}</Error>}

        <Form onSubmit={handleSubmit}>
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          <Button type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Form>

        <Divider>or</Divider>

        <SSOButton type="button" onClick={handleSSO}>
          🔑 Sign in with SSO
        </SSOButton>

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <Button type="button" onClick={() => onLogin({ name: 'Guest', email: 'guest@local', role: 'editor' }, 'guest-token')} style={{ width: '100%', background: 'linear-gradient(135deg, #28a745 0%, #20923d 100%)' }}>
            Let me try it
          </Button>
          <div style={{ fontSize: '13px', color: '#8b949e', marginTop: '12px', fontWeight: '500' }}>⚠️ All data stored locally in browser - not shared between users</div>
        </div>
        
        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '13px', color: '#57606a' }}>
          Don't have an account? <Link onClick={() => alert('Sign up functionality coming soon!')}>Sign up</Link>
        </div>
      </Card>
    </Container>
  );
}

export default LoginScreen;
